import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReachChange, ReachChangeKind, ReachPerson } from '../sampleData';

import { PersonAvatar } from './PersonAvatar';
import { useReasonLabel } from './useReasonLabel';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * Who a change reaches, person by person (brief §4.2; Insights wireframe 4):
 * before → after in the title, filter chips with counts (Everyone by default,
 * so the baseline shows), search, Name · Department · Why · Change, a footer
 * note and Close. Closing returns to the page with pending changes kept.
 * The hover peek and person card are not in this build (decision 7).
 *
 * Opened on an unchanged level (`showChange` false, decision 22) it is just
 * the list: no before → after, no chips and no Change column, as in Insights.
 */
type Filter = 'everyone' | ReachChangeKind;

const FILTERS: readonly Filter[] = ['everyone', 'gaining', 'losing', 'keeping'];

const CHANGE_LOOK: Record<ReachChangeKind, { icon: string; color: string; sign: string }> = {
  gaining: { icon: 'add_circle', color: 'var(--color-success-80)', sign: '+ ' },
  losing: { icon: 'remove_circle', color: 'var(--color-error-80)', sign: '− ' },
  keeping: { icon: 'check_circle', color: 'var(--fg-3)', sign: '' },
};

export type PeopleModalProps = {
  open: boolean;
  change: ReachChange;
  people: ReachPerson[];
  /** False while nothing pending moves who can log time. */
  showChange: boolean;
  /**
   * Replaces the before → after title — Project details' "Who can log time
   * after this change" (docs/briefs/project-details-v2, decision 6).
   */
  title?: string;
  onClose: () => void;
};

export const PeopleModal = ({
  open,
  change,
  people,
  showChange,
  title,
  onClose,
}: PeopleModalProps) => {
  const { t } = useTranslation('pmDetails');
  const [filter, setFilter] = useState<Filter>('everyone');
  const [search, setSearch] = useState('');

  const counts: Record<Filter, number> = {
    everyone: people.length,
    gaining: change.gaining.length,
    losing: change.losing.length,
    keeping: change.keeping.length,
  };

  const shown = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('da');
    // With no chips to show it, a filter left over from a changed level must not apply.
    const active = showChange ? filter : 'everyone';
    return people.filter(
      (person) =>
        (active === 'everyone' || person.change === active) &&
        (query === '' || person.name.toLocaleLowerCase('da').includes(query)),
    );
  }, [people, filter, search, showChange]);

  const why = useReasonLabel();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-automation-id="settings-people-modal"
    >
      <DialogTitle>
        {title ??
          (showChange
            ? t('reach.Title', { before: change.before, after: change.after, count: change.after })
            : t('reach.TitleUnchanged', { count: change.after }))}
      </DialogTitle>
      <DialogContent>
        {showChange ? (
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {FILTERS.map((key) => (
              <Chip
                key={key}
                clickable
                variant={filter === key ? 'filled' : 'outlined'}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
                data-automation-id={`settings-people-filter-${key}`}
                label={`${key === 'everyone' ? '' : CHANGE_LOOK[key].sign}${t(`reach.filter.${key}`)} · ${counts[key]}`}
              />
            ))}
          </Stack>
        ) : null}
        <TextField
          size="small"
          fullWidth
          value={search}
          placeholder={t('reach.Search')}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              size: 'small',
              startAdornment: <MaterialSymbol name="search" size={16} color="var(--fg-3)" />,
            },
            htmlInput: {
              'aria-label': t('reach.Search'),
              'data-automation-id': 'settings-people-search',
            },
          }}
          sx={{ mb: 2 }}
        />
        <Table size="small" data-automation-id="settings-people-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('reach.col.Name')}</TableCell>
              <TableCell>{t('reach.col.Department')}</TableCell>
              <TableCell>{t('reach.col.Why')}</TableCell>
              {showChange ? <TableCell>{t('reach.col.Change')}</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((person) => {
              const look = CHANGE_LOOK[person.change];
              return (
                <TableRow
                  key={person.id}
                  data-automation-id="settings-people-row"
                  data-change={person.change}
                >
                  <TableCell>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                      <PersonAvatar name={person.name} />
                      {person.name}
                    </Stack>
                  </TableCell>
                  <TableCell>{person.departmentName}</TableCell>
                  <TableCell>{why(person.reason)}</TableCell>
                  {showChange ? (
                    <TableCell>
                      {/* Icon AND label, never an icon alone (§4.2). */}
                      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                        <MaterialSymbol name={look.icon} size={16} color={look.color} />
                        {t(`reach.filter.${person.change}`)}
                      </Stack>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {shown.length === 0 ? (
          <Typography variant="bodyRegular" component="p" sx={{ color: 'var(--fg-3)', p: 2 }}>
            {t('reach.NoMatches')}
          </Typography>
        ) : null}
      </DialogContent>
      {/* The note sits beside Close, so it shows without scrolling the list. */}
      <DialogActions sx={{ justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="label" component="p" sx={{ color: 'var(--fg-3)' }}>
          {t('reach.Footer')}
        </Typography>
        <Button variant="contained" onClick={onClose} data-automation-id="settings-people-close">
          {t('reach.Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
